import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { OnboardingAnswers, UserCreativeProfile, MemoirEntry } from '@/data/types';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface UserState {
  onboardingCompleted: boolean;
  onboardingAnswers: OnboardingAnswers | null;
  creativeProfile: UserCreativeProfile;
  completedProjects: string[];
  projectProgress: Record<string, number[]>;
  memoirEntries: MemoirEntry[];
  sideQuestVotes: Record<string, 1 | -1>;
  supplyMode: string;
}

interface UserContextType extends UserState {
  setOnboardingAnswers: (answers: OnboardingAnswers) => void;
  completeChapter: (projectId: string, chapterIndex: number) => void;
  completeProject: (projectId: string) => void;
  addMemoirEntry: (entry: MemoirEntry) => void;
  voteSideQuest: (id: string, vote: 1 | -1) => void;
  setSupplyMode: (mode: string) => void;
  resetOnboarding: () => void;
  getChapterProgress: (projectId: string) => number[];
  isProjectCompleted: (projectId: string) => boolean;
}

const defaultProfile: UserCreativeProfile = {
  traitCreative: 50, traitAnalytical: 50, traitPhysical: 50,
  traitExpressive: 50, traitSocial: 50,
  prefHandsOn: 50, prefStructured: 50, prefQuickWin: 50, prefSocial: 50,
  prefDecorative: 50, prefDisplayable: 50, prefLowMess: 50, prefLowCost: 50,
  prefCalming: 50, prefGuided: 50, prefSentimental: 50, prefShortSession: 50,
  completionsCount: 0,
};

const defaultState: UserState = {
  onboardingCompleted: false,
  onboardingAnswers: null,
  creativeProfile: defaultProfile,
  completedProjects: [],
  projectProgress: {},
  memoirEntries: [],
  sideQuestVotes: {},
  supplyMode: 'household',
};

const UserContext = createContext<UserContextType | undefined>(undefined);

function loadState(): UserState {
  try {
    const stored = localStorage.getItem('shrug-user-state');
    if (stored) return { ...defaultState, ...JSON.parse(stored) };
  } catch {}
  return defaultState;
}

function seedProfileFromAnswers(answers: OnboardingAnswers): UserCreativeProfile {
  const p = { ...defaultProfile };
  if (answers.motivation === 'bored') { p.prefQuickWin = 70; p.prefCalming = 40; }
  if (answers.motivation === 'creative-outlet') { p.traitCreative = 75; p.prefHandsOn = 70; }
  if (answers.motivation === 'calmer') { p.prefCalming = 80; p.traitExpressive = 65; }
  if (answers.motivation === 'learn-skill') { p.traitAnalytical = 70; p.prefStructured = 75; }
  if (answers.motivation === 'better-hobbies') { p.prefQuickWin = 65; p.prefGuided = 65; }
  if (answers.motivation === 'social') { p.traitSocial = 80; p.prefSocial = 80; }
  if (answers.priorExperience === 'none') { p.prefGuided = 80; p.prefStructured = 75; }
  if (answers.priorExperience === 'tried-few') { p.prefGuided = 65; }
  if (answers.priorExperience === 'fell-off') { p.prefQuickWin = 70; }
  if (answers.priorExperience === 'usually-one') { p.prefStructured = 55; }
  if (answers.priorExperience === 'very-creative') { p.traitCreative = 80; p.prefGuided = 30; }
  const frictions = (answers.pastFriction || '').split(',');
  if (frictions.includes('never-started')) { p.prefGuided = Math.max(p.prefGuided, 75); }
  if (frictions.includes('lost-interest')) { p.prefQuickWin = Math.max(p.prefQuickWin, 70); p.prefShortSession = 70; }
  if (frictions.includes('too-complicated')) { p.prefStructured = Math.max(p.prefStructured, 75); p.prefGuided = Math.max(p.prefGuided, 70); }
  if (frictions.includes('expensive')) { p.prefLowCost = 85; }
  if (frictions.includes('no-time')) { p.prefShortSession = 80; }
  if (frictions.includes('no-next-step')) { p.prefGuided = Math.max(p.prefGuided, 75); }
  if (answers.environment === 'indoors') { p.prefLowMess = 60; }
  if (answers.environment === 'outdoors') { p.traitPhysical = Math.max(p.traitPhysical, 70); }
  if (answers.environment === 'leave-house') { p.traitPhysical = Math.max(p.traitPhysical, 65); p.traitSocial = Math.max(p.traitSocial, 55); }
  if (answers.environment === 'cozy-home') { p.prefCalming = Math.max(p.prefCalming, 65); p.prefLowMess = 65; }
  if (answers.availableTime === '10-20') { p.prefShortSession = 90; p.prefQuickWin = 80; }
  if (answers.availableTime === '20-45') { p.prefShortSession = 70; }
  if (answers.availableTime === '45-90') { p.prefShortSession = 40; }
  if (answers.availableTime === '90-plus') { p.prefShortSession = 20; }
  if (answers.availableTime === 'weekends') { p.prefShortSession = 30; }
  if (answers.setupTolerance === 'no-setup') { p.prefLowMess = 90; p.prefLowCost = 80; }
  if (answers.setupTolerance === 'little-setup') { p.prefLowMess = 70; }
  if (answers.setupTolerance === 'some-supplies') { p.prefLowMess = 50; }
  if (answers.setupTolerance === 'full-project') { p.prefLowMess = 30; p.prefHandsOn = 75; }
  if (answers.setupTolerance === 'no-limit') { p.prefLowMess = 15; p.prefHandsOn = 80; }
  if (answers.resultPreference === 'beautiful') { p.prefDisplayable = 85; p.prefDecorative = 80; }
  if (answers.resultPreference === 'useful') { p.prefHandsOn = 75; }
  if (answers.resultPreference === 'process') { p.prefCalming = Math.max(p.prefCalming, 80); }
  if (answers.resultPreference === 'playful') { p.traitExpressive = 75; p.prefQuickWin = 65; }
  if (answers.resultPreference === 'mastery') { p.traitAnalytical = 75; p.prefStructured = 70; }
  if (answers.resultPreference === 'shareable') { p.traitSocial = Math.max(p.traitSocial, 70); p.prefSocial = 70; }
  if (answers.creativeStyle === 'structured') { p.prefStructured = Math.max(p.prefStructured, 80); p.prefGuided = Math.max(p.prefGuided, 75); }
  if (answers.creativeStyle === 'exploratory') { p.prefStructured = Math.min(p.prefStructured, 35); p.traitExpressive = Math.max(p.traitExpressive, 70); }
  if (answers.socialContext === 'just-me') { p.traitSocial = Math.min(p.traitSocial, 25); p.prefSocial = 20; }
  if (answers.socialContext === 'mostly-me') { p.traitSocial = Math.min(p.traitSocial, 40); }
  if (answers.socialContext === 'partner') { p.traitSocial = Math.max(p.traitSocial, 65); p.prefSocial = 65; }
  if (answers.socialContext === 'friends') { p.traitSocial = Math.max(p.traitSocial, 80); p.prefSocial = 80; }
  if (answers.socialContext === 'family') { p.traitSocial = Math.max(p.traitSocial, 70); p.prefSocial = 70; }
  if (answers.socialContext === 'both') { p.traitSocial = 60; p.prefSocial = 55; }
  const picks = (answers.interestPicks || '').split(',');
  if (picks.includes('drawing')) { p.traitCreative = Math.max(p.traitCreative, 70); p.traitExpressive = Math.max(p.traitExpressive, 65); }
  if (picks.includes('journaling')) { p.traitExpressive = Math.max(p.traitExpressive, 70); p.prefCalming = Math.max(p.prefCalming, 60); }
  if (picks.includes('painting')) { p.traitCreative = Math.max(p.traitCreative, 75); p.prefDisplayable = Math.max(p.prefDisplayable, 65); }
  if (picks.includes('diy')) { p.prefHandsOn = Math.max(p.prefHandsOn, 75); p.traitPhysical = Math.max(p.traitPhysical, 65); }
  if (picks.includes('cooking')) { p.prefHandsOn = Math.max(p.prefHandsOn, 65); p.traitPhysical = Math.max(p.traitPhysical, 60); }
  if (picks.includes('gardening')) { p.traitPhysical = Math.max(p.traitPhysical, 65); p.prefCalming = Math.max(p.prefCalming, 65); }
  if (picks.includes('photography')) { p.traitCreative = Math.max(p.traitCreative, 70); p.traitExpressive = Math.max(p.traitExpressive, 65); }
  if (picks.includes('outdoor')) { p.traitPhysical = Math.max(p.traitPhysical, 70); }
  if (picks.includes('crafts')) { p.prefHandsOn = Math.max(p.prefHandsOn, 75); p.traitCreative = Math.max(p.traitCreative, 65); }
  if (picks.includes('music')) { p.traitExpressive = Math.max(p.traitExpressive, 75); p.traitCreative = Math.max(p.traitCreative, 65); }
  if (picks.includes('movement')) { p.traitPhysical = Math.max(p.traitPhysical, 75); p.prefCalming = Math.max(p.prefCalming, 60); }
  return p;
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UserState>(loadState);
  const { user } = useAuth();

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('shrug-user-state', JSON.stringify(state));
  }, [state]);

  // Sync state TO database when user is authenticated and state changes
  const syncToDb = useCallback(async (newState: UserState) => {
    if (!user) return;
    
    // Sync preferences (onboarding + profile)
    try {
      await supabase.from('user_preferences').upsert({
        user_id: user.id,
        onboarding_answers: newState.onboardingAnswers as any,
        creative_profile: newState.creativeProfile as any,
        supply_mode: newState.supplyMode,
      }, { onConflict: 'user_id' });
    } catch {}

    // Sync project progress
    for (const [projectId, chapters] of Object.entries(newState.projectProgress)) {
      const isCompleted = newState.completedProjects.includes(projectId);
      try {
        await supabase.from('user_projects').upsert({
          user_id: user.id,
          project_id: projectId,
          status: isCompleted ? 'completed' : 'in_progress',
          progress: chapters,
          completed_at: isCompleted ? new Date().toISOString() : null,
        }, { onConflict: 'user_id,project_id' });
      } catch {}
    }
  }, [user]);

  // Load state FROM database on login
  useEffect(() => {
    if (!user) return;
    
    (async () => {
      try {
        // Load preferences
        const { data: prefs } = await supabase
          .from('user_preferences')
          .select('*')
          .maybeSingle();
        
        // Load projects
        const { data: projects } = await supabase
          .from('user_projects')
          .select('*');

        if (prefs || (projects && projects.length > 0)) {
          setState(prev => {
            const newState = { ...prev };
            
            if (prefs) {
              if (prefs.onboarding_answers) {
                newState.onboardingCompleted = true;
                newState.onboardingAnswers = prefs.onboarding_answers as any;
                newState.creativeProfile = (prefs.creative_profile as any) || prev.creativeProfile;
              }
              if (prefs.supply_mode) newState.supplyMode = prefs.supply_mode;
            }

            if (projects && projects.length > 0) {
              const progress: Record<string, number[]> = {};
              const completed: string[] = [];
              for (const p of projects) {
                progress[p.project_id] = (p.progress as number[]) || [];
                if (p.status === 'completed') completed.push(p.project_id);
              }
              newState.projectProgress = { ...prev.projectProgress, ...progress };
              newState.completedProjects = [...new Set([...prev.completedProjects, ...completed])];
            }

            return newState;
          });
        }
      } catch {}
    })();
  }, [user]);

  const updateState = (updater: (s: UserState) => UserState) => {
    setState(prev => {
      const next = updater(prev);
      syncToDb(next);
      return next;
    });
  };

  const setOnboardingAnswers = (answers: OnboardingAnswers) => {
    updateState(s => ({
      ...s,
      onboardingCompleted: true,
      onboardingAnswers: answers,
      creativeProfile: seedProfileFromAnswers(answers),
    }));
  };

  const completeChapter = (projectId: string, chapterIndex: number) => {
    updateState(s => {
      const progress = { ...s.projectProgress };
      const chapters = progress[projectId] || [];
      const isFirstStart = chapters.length === 0 && chapterIndex === 0;
      if (!chapters.includes(chapterIndex)) {
        progress[projectId] = [...chapters, chapterIndex];
      }
      // Increment project_starts_used on first chapter of a new project
      if (isFirstStart && user) {
        supabase.from('user_subscriptions')
          .select('project_starts_used')
          .eq('user_id', user.id)
          .maybeSingle()
          .then(({ data }) => {
            if (data) {
              supabase.from('user_subscriptions')
                .update({ project_starts_used: (data.project_starts_used || 0) + 1 })
                .eq('user_id', user.id)
                .then(() => {});
            } else {
              supabase.from('user_subscriptions')
                .insert({ user_id: user.id, project_starts_used: 1 })
                .then(() => {});
            }
          });
      }
      return { ...s, projectProgress: progress };
    });
  };

  const completeProject = (projectId: string) => {
    updateState(s => ({
      ...s,
      completedProjects: s.completedProjects.includes(projectId)
        ? s.completedProjects
        : [...s.completedProjects, projectId],
      creativeProfile: {
        ...s.creativeProfile,
        completionsCount: s.creativeProfile.completionsCount + 1,
      },
    }));
  };

  const addMemoirEntry = (entry: MemoirEntry) => {
    updateState(s => ({ ...s, memoirEntries: [...s.memoirEntries, entry] }));
  };

  const voteSideQuest = (id: string, vote: 1 | -1) => {
    updateState(s => ({ ...s, sideQuestVotes: { ...s.sideQuestVotes, [id]: vote } }));
  };

  const setSupplyMode = (mode: string) => {
    updateState(s => ({ ...s, supplyMode: mode }));
  };

  const resetOnboarding = () => {
    updateState(() => defaultState);
  };

  const getChapterProgress = (projectId: string): number[] =>
    state.projectProgress[projectId] || [];

  const isProjectCompleted = (projectId: string): boolean =>
    state.completedProjects.includes(projectId);

  return (
    <UserContext.Provider value={{
      ...state,
      setOnboardingAnswers, completeChapter, completeProject,
      addMemoirEntry, voteSideQuest, setSupplyMode, resetOnboarding,
      getChapterProgress, isProjectCompleted,
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within UserProvider');
  return ctx;
}
